import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../../../../global-state/themeContext';
import { useStoreInformation } from '../../../../../../hooks';
import { Icon, Icons, Tooltip, Typography } from '../../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../../../../theme';
import { homeTranslations } from '../../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../../utils';
import { HelpASaleCard } from './HelpASaleOverview.styles';

type HelpASaleOverviewProps = {
  uplift: number;
  hasSales: number;
  conversionRate: number;
  hasCount: number;
  hasCountPerVisitor: number;
};

type GridItemProps = {
  title: string;
  value: string;
  icon: Icons;
  metric: string;
  tooltipText: string;
};

const GridSection: FC<GridItemProps> = ({ title, value, icon, metric, tooltipText }) => {
  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  return (
    <HelpASaleCard
      data-testid="overview-grid-section"
      metricName={metric}
      flex={1}
      gap={isMobileView ? [1] : [2]}
      padding={[2, 3]}
      borderRadius={[2]}
      alignItems="center"
    >
      <RowFlexContainer flex={1} gap={[1]} alignItems="center">
        <Icon icon={icon} size="small" />
        <Typography type="caption">{title}</Typography>
      </RowFlexContainer>
      <RowFlexContainer alignItems="center" gap={[1]}>
        <Typography type="button">{value}</Typography>
        <Tooltip
          text={
            <Typography type="caption" color="white">
              {tooltipText}
            </Typography>
          }
          position="top"
          arrowedToolTip
        >
          <Icon icon={Icons.INFO} size="small" />
        </Tooltip>
      </RowFlexContainer>
    </HelpASaleCard>
  );
};

export const HelpASaleOverview: FC<HelpASaleOverviewProps> = ({
  uplift,
  hasSales,
  conversionRate,
  hasCount,
  hasCountPerVisitor,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const isDarkMode = mode === 'dark';
  const { currencyCode } = useStoreInformation();
  const { helpASale: helpASaleTranslations, salesNet } = homeTranslations;

  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  return (
    <ColumnFlexContainer data-testid="help-a-sale-widget-overview-wrapper" padding={[4]} height="100%">
      <RowFlexContainer
        backgroundColor={isDarkMode ? 'backgroundSelectedHover' : 'backgroundSelectedDark'}
        data-testid="help-a-sale-widget-header"
        justifyContent="between"
        alignItems="center"
        borderRadius={[2]}
        hasBorder
        padding={[2, 4, 1]}
      >
        <ColumnFlexContainer gap={isMobileView ? [2] : [1]}>
          <RowFlexContainer gap={[1]}>
            <Typography type="body4">
              {t(helpASaleTranslations.upliftText.key, helpASaleTranslations.upliftText.defaultValue)}
            </Typography>
            <Tooltip
              text={
                <Typography type="caption" color="white">
                  {t(
                    helpASaleTranslations.upliftTooltipText.key,
                    helpASaleTranslations.upliftTooltipText.defaultValue,
                  )}
                </Typography>
              }
              position="top"
              arrowedToolTip
            >
              <Icon icon={Icons.INFO} size="small" />
            </Tooltip>
          </RowFlexContainer>

          <Typography type="h4">{formatAmountWithSeparator(uplift, '%')}</Typography>
        </ColumnFlexContainer>
        <Icon icon={Icons.ARROW} size="large" rotate="right" />
      </RowFlexContainer>
      <ColumnFlexContainer
        data-testid="help-a-sale-widget-content"
        height="100%"
        maxHeight="100%"
        overflow="scroll"
        gap={[2]}
        margin={[2, 0, 0]}
      >
        <GridSection
          title={t(salesNet.title.key, salesNet.title.defaultValue)}
          value={formatAmountWithSeparator(hasSales, currencyCode, 0)}
          icon={Icons.MONEYBAG}
          metric="sales"
          tooltipText={t(
            helpASaleTranslations.upliftSalesTooltipText.key,
            helpASaleTranslations.upliftSalesTooltipText.defaultValue,
          )}
        />
        <GridSection
          title={t(helpASaleTranslations.conversionText.key, helpASaleTranslations.conversionText.defaultValue)}
          value={formatAmountWithSeparator(conversionRate, '%')}
          icon={Icons.CONVERSION}
          metric="conversion"
          tooltipText={t(
            helpASaleTranslations.conversionPercentageTooltipText.key,
            helpASaleTranslations.conversionPercentageTooltipText.defaultValue,
          )}
        />

        <GridSection
          title={t(helpASaleTranslations.hasCountText.key, helpASaleTranslations.hasCountText.defaultValue)}
          value={formatAmountWithSeparator(hasCount, undefined, 0)}
          icon={Icons.BAG}
          metric="count"
          tooltipText={t(
            helpASaleTranslations.customerFacingLookupsTooltipText.key,
            helpASaleTranslations.customerFacingLookupsTooltipText.defaultValue,
          )}
        />
        <GridSection
          title={t(
            helpASaleTranslations.hasPerVisitorsText.key,
            helpASaleTranslations.hasPerVisitorsText.defaultValue,
          )}
          value={formatAmountWithSeparator(hasCountPerVisitor, '%')}
          icon={Icons.NEWMEMBER}
          metric="visitor"
          tooltipText={t(
            helpASaleTranslations.customerFacingLookupsPerVisitorTooltipText.key,
            helpASaleTranslations.customerFacingLookupsPerVisitorTooltipText.defaultValue,
          )}
        />
      </ColumnFlexContainer>
    </ColumnFlexContainer>
  );
};
